import React from 'react';
import styled from 'styled-components';
import { colors } from '@/constants/colors';
import useAuthStore from '@/store/authStore';

interface Props {
   children: React.ReactNode;
}

interface State {
   error: Error | null;
}

/**
 * Last line of defence against the blank white page.
 *
 * React unmounts the WHOLE tree when a render throws and nothing catches it,
 * so before this existed any single component error left the user staring at
 * an empty window with no message, no button and no way back — the exact
 * complaint that started the 2026-07-22 investigation. Whatever the cause,
 * the user now gets words and two ways out.
 */
class AppErrorBoundary extends React.Component<Props, State> {
   state: State = { error: null };

   static getDerivedStateFromError(error: Error): State {
      return { error };
   }

   componentDidCatch(error: Error, info: React.ErrorInfo) {
      // Keep the detail in the console — support asks for it by screenshot.
      console.error('Unhandled UI error:', error, info.componentStack);
   }

   handleReload = () => {
      window.location.reload();
   };

   handleSignOut = () => {
      try {
         useAuthStore.getState().clearAuth();
      } catch {
         /* clearing must never block the escape hatch */
      }
      window.location.href = '/';
   };

   render() {
      if (!this.state.error) return this.props.children;

      return (
         <Overlay>
            <Card>
               <Title>Something went wrong</Title>
               <Desc>
                  This page could not be displayed. Your data is safe. Please
                  reload — if it keeps happening, sign out and sign in again.
               </Desc>
               <PrimaryBtn onClick={this.handleReload}>Reload page</PrimaryBtn>
               <SecondaryBtn onClick={this.handleSignOut}>Sign out</SecondaryBtn>
            </Card>
         </Overlay>
      );
   }
}

const Overlay = styled.div`
   position: fixed;
   inset: 0;
   z-index: 3000;
   display: flex;
   align-items: center;
   justify-content: center;
   padding: 16px;
   background: linear-gradient(135deg, #0b1030 0%, #1a1f4a 50%, #0f1535 100%);
`;

const Card = styled.div`
   width: min(420px, 100%);
   padding: 28px 24px 22px;
   border-radius: 18px;
   background: rgba(20, 24, 56, 0.9);
   border: 1px solid rgba(255, 255, 255, 0.1);
   box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
   color: white;
   text-align: center;
`;

const Title = styled.h2`
   font-size: 22px;
   font-weight: 800;
   color: white;
   margin-bottom: 10px;
`;

const Desc = styled.p`
   font-size: 13.5px;
   color: rgba(255, 255, 255, 0.7);
   line-height: 1.55;
   margin-bottom: 20px;
`;

const PrimaryBtn = styled.button`
   width: 100%;
   padding: 12px 14px;
   border-radius: 12px;
   font-size: 14px;
   font-weight: 800;
   color: #0b1030;
   background: ${colors.SECONDARY};
   border: none;
   cursor: pointer;
   &:hover {
      filter: brightness(1.05);
   }
`;

const SecondaryBtn = styled.button`
   width: 100%;
   margin-top: 10px;
   padding: 11px 14px;
   border-radius: 10px;
   font-size: 13px;
   font-weight: 700;
   background: rgba(255, 255, 255, 0.06);
   color: white;
   border: 1px solid rgba(255, 255, 255, 0.15);
   cursor: pointer;
   &:hover {
      background: rgba(255, 255, 255, 0.1);
   }
`;

export default AppErrorBoundary;
